import React from 'react'
import { ActivityIndicator } from 'react-native'

import { i18n } from '@localization'
import { NavigationContainer } from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { CustomerBasicDetailsV2, PanDetails, PanDetailsV2 } from '@zet/customer'
import AddCustomerHeader from '@zet/customer/src/components/addCustomerHeader'
import VerifySecondaryPhoneNumber from '@zet/customer/src/screens/verifySecondaryPhoneNumber'
import VerifySecondaryPhoneOtp from '@zet/customer/src/screens/verifySecondaryPhoneOtp'
import { colors, Stack } from '@zet/design-system'
import ApplicationProcess from '@zet/feature-brands/screens/applicationProcess'
import { BrandBenefits } from '@zet/feature-brands/screens/brandBenefits'
import CrossSelling from '@zet/feature-brands/screens/brandDetails/crossSelling'
import ChoiceConnect from '@zet/feature-brands/screens/choiceConnect'
import { InEligibleBrandsList } from '@zet/feature-brands/screens/inEligibleBrandsList'
import ProductPageWebflow from '@zet/feature-brands/screens/productPageWebflow'
import { CreditReport } from '@zet/feature-credit-score'
import BrandsCC from '@zet/integrated-journeys'
import {
  CustomerBasicDetails,
  CustomerInfo,
  CustomerLoading,
  CustomerPanDetails,
  IntegrationFlowHeader,
} from '@zet/integration-flow'
import { navigationRef } from '@zet/utils/navigation'
import {
  CustomerDetails,
  CustomHeader,
  OtpVerification,
  QuickJourneyPanDetails,
} from '@zet/zet-pro-quick-journey'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'

import BottomSheetHeader from '@components/bottomSheetHeader'
import MissingFieldsPopup from '@components/missingFieldsPopup'
import MissingFieldsBottomSheet from '@components/missingFieldsPopup/missingFieldsBottomSheet'

import { EligibilityCriteria } from '@screens/eligibilityCriteria'
import ExperianTermsOfUse from '@screens/experianTermsOfUse'
import ProductList from '@screens/productList'

import { widgetFlowUrls } from '@utils/Constants'

import BasicDetails from '../webflow/screens/addCustomer/basicDetails'
import EmploymentDetailsWF from '../webflow/screens/addCustomer/employmentDetailsWF'
import NameAndPhoneNumber from '../webflow/screens/addCustomer/nameAndPhoneNumber'
import OtpLessVerification from '../webflow/screens/addCustomer/otpLessVerification'
import OtpScreen from '../webflow/screens/addCustomer/otpScreen'
import { ApplicationTracking } from '../webflow/screens/testBrandApplication/applicationTracking'
import { EnterNameAndPan } from '../webflow/screens/testBrandApplication/enterNameAndPan'
import LeadApplicationStatus from '../webflow/screens/testBrandApplication/leadApplicationStatus'
import { VKyc } from '../webflow/screens/testBrandApplication/vKyc'
import UserHome from '../webflow/screens/userHome'
import UserLogin from '../webflow/screens/userLogin'
import UserOffers from '../webflow/screens/userOffers'
import VerifyOtpAfterShare from '../webflow/screens/verifyOtpAfterShare'
import ThankYouAfterVerify from '../webflow/screens/verifyOtpAfterShare/ThankYouAfterVerify'
import linking from './linking'

const WebFlowStack = createNativeStackNavigator()

const screenWithNoHeader = { headerShown: false }

function Root() {
  const isWidgetFlow = widgetFlowUrls.includes(window.location.pathname)

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      documentTitle={{
        enabled: false,
      }}
    >
      <WebFlowStack.Navigator>
        <WebFlowStack.Screen
          options={{
            headerShown: false,
          }}
          name="LoggedIn"
          component={isWidgetFlow ? widgetFlow : WebFlow}
        />
      </WebFlowStack.Navigator>
      <MissingFieldsBottomSheet />
    </NavigationContainer>
  )
}

function WebFlow() {
  const pathname = window?.location?.pathname || ''
  const isUserFlowRoute = pathname === '/user' || pathname?.startsWith('/user/')
  const { isBMSFlow, isLoading, isJfsFlow } = useGetAgentDetailsAndTheme()

  if (!isUserFlowRoute && isLoading) {
    return (
      <Stack
        style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
      >
        <ActivityIndicator size="large" />
      </Stack>
    )
  }

  return (
    <WebFlowStack.Navigator>
      {isBMSFlow ? (
        <WebFlowStack.Screen
          name="QuickCustomerDetails"
          component={CustomerDetails}
          options={{
            header: () => (
              <CustomHeader
                logoColor={colors.primary[100]}
                btnColor={colors.grays[30]}
                btnTextColor={colors.grays[70]}
              />
            ),
          }}
        />
      ) : isJfsFlow ? (
        <WebFlowStack.Screen
          name="CustomerLoading"
          component={CustomerLoading}
          options={{ header: () => <IntegrationFlowHeader /> }}
        />
      ) : (
        <WebFlowStack.Screen
          name="EnterNameAndPhoneNumber"
          component={NameAndPhoneNumber}
        />
      )}
      <WebFlowStack.Screen
        name="UserLogin"
        component={UserLogin}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="UserOtp"
        component={UserLogin}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="UserBasicDetails"
        component={UserLogin}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="UserPanDetails"
        component={UserLogin}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="UserOffers"
        component={UserOffers}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="UserHome"
        component={UserHome}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="LeadApplicationStatus"
        component={LeadApplicationStatus}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="EnterNumberAndPan"
        component={EnterNameAndPan}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="Vkyc"
        component={VKyc}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="tracking"
        component={ApplicationTracking}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="CheckCreditScore"
        component={NameAndPhoneNumber}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="CreditReport"
        component={CreditReport}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen name="OtpScreen" component={OtpScreen} />
      <WebFlowStack.Screen
        name="BasicDetails"
        component={BasicDetails}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="CustomerBasicDetailsV2"
        component={CustomerBasicDetailsV2}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="PanDetails"
        component={PanDetails}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="PanDetailsV2"
        component={PanDetailsV2}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="EmploymentDetailsWF"
        component={EmploymentDetailsWF}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="VerifyOtpAfterShare"
        component={VerifyOtpAfterShare}
      />
      <WebFlowStack.Screen
        name="ThankYouAfterVerify"
        component={ThankYouAfterVerify}
        options={{ header: () => <AddCustomerHeader forVerifyingOtp={true} /> }}
      />
      <WebFlowStack.Screen
        name="BrandPage"
        component={ProductList}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="InEligibleBrandsList"
        component={InEligibleBrandsList}
        options={screenWithNoHeader}
      />
      <WebFlowStack.Screen
        name="EligibilityCriteria"
        component={EligibilityCriteria}
        options={{
          header: () => (
            <BottomSheetHeader
              isSecondaryIcon={false}
              primaryIcon="Close"
              isGoBack={true}
              title={i18n.t('navigationHeading.documentsRequired')}
              isHeaderShadow={true}
            />
          ),
        }}
      />
      <WebFlowStack.Screen
        name="ApplicationProcess"
        component={ApplicationProcess}
        options={{
          header: () => (
            <BottomSheetHeader
              isSecondaryIcon={false}
              primaryIcon="Close"
              isGoBack={true}
              title={i18n.t('navigationHeading.applicationProcess')}
              isHeaderShadow={true}
            />
          ),
        }}
      />
      <WebFlowStack.Screen
        name="BrandBenefits"
        component={BrandBenefits}
        options={{
          header: () => (
            <BottomSheetHeader
              isSecondaryIcon={false}
              primaryIcon="Close"
              isGoBack={true}
              title={i18n.t('navigationHeading.brandBenefits')}
              isHeaderShadow={true}
            />
          ),
        }}
      />
      <WebFlowStack.Screen
        name="BrandsCC"
        component={BrandsCC}
        options={{ headerShown: false }}
      />
      <WebFlowStack.Screen
        name="BrandsLOS"
        component={BrandsCC}
        options={{ headerShown: false }}
      />
      {/* <WebFlowStack.Screen
        name="RedirectInjection"
        component={RedirectInjection}
        options={{ headerShown: false }}
      /> */}
      <WebFlowStack.Screen
        name="CrossSelling"
        component={CrossSelling}
        options={{
          header: () => (
            <BottomSheetHeader
              isSecondaryIcon={false}
              primaryIcon="Close"
              isGoBack={true}
              title={'Load Recommendations'}
              isHeaderShadow={true}
            />
          ),
        }}
      />
      <WebFlowStack.Screen
        name="OtpLessVerification"
        component={OtpLessVerification}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="MissingFieldsPopup"
        component={MissingFieldsPopup}
        options={{ headerShown: false }}
      />
      <WebFlowStack.Screen
        name="ProductPageWebflow"
        component={ProductPageWebflow}
        options={{ headerShown: false }}
      />
      <WebFlowStack.Screen
        name="VerifySecondaryPhoneNumber"
        component={VerifySecondaryPhoneNumber}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="VerifySecondaryPhoneOtp"
        component={VerifySecondaryPhoneOtp}
        options={{ header: () => <AddCustomerHeader /> }}
      />
      <WebFlowStack.Screen
        name="ExperianTermsOfUse"
        component={ExperianTermsOfUse}
        options={screenWithNoHeader}
      />

      <WebFlowStack.Screen
        name="OtpVerification"
        component={OtpVerification}
        options={{
          header: () => (
            <CustomHeader
              logoColor={colors.primary[100]}
              btnColor={colors.grays[30]}
              btnTextColor={colors.grays[70]}
            />
          ),
        }}
      />
      <WebFlowStack.Screen
        name="QuickJourneyPanDetails"
        component={QuickJourneyPanDetails}
        options={{
          header: () => (
            <CustomHeader
              logoColor={colors.primary[100]}
              btnColor={colors.grays[30]}
              btnTextColor={colors.grays[70]}
            />
          ),
        }}
      />
      <WebFlowStack.Group>
        <WebFlowStack.Screen
          name="CustomerInfo"
          component={CustomerInfo}
          options={{ header: () => <IntegrationFlowHeader /> }}
        />
        <WebFlowStack.Screen
          name="CustomerBasicDetails"
          component={CustomerBasicDetails}
          options={{ header: () => <IntegrationFlowHeader /> }}
        />
        <WebFlowStack.Screen
          name="CustomerPanDetails"
          component={CustomerPanDetails}
          options={{ header: () => <IntegrationFlowHeader /> }}
        />
      </WebFlowStack.Group>
    </WebFlowStack.Navigator>
  )
}

function widgetFlow() {
  return (
    <WebFlowStack.Navigator>
      <WebFlowStack.Screen
        name="ChoiceConnect"
        component={ChoiceConnect}
        options={screenWithNoHeader}
      />
    </WebFlowStack.Navigator>
  )
}

export default Root
